import { mutateAction } from "@libs/actions/mutateAction";
import { randomStringGen } from "@libs/helper";
import { useData } from "@providers/context";
import { useSession } from "next-auth/react";
import { useState, useRef, useCallback, useEffect } from "react";

// Safe router hook that handles the mounting issue
function useSafeRouter() {
  const [router, setRouter] = useState<any>(null);

  useEffect(() => {
    // Dynamically import and use router only on client side
    if (typeof window !== "undefined") {
      import("next/router").then(({ useRouter }) => {
        try {
          const routerInstance = useRouter();
          if (routerInstance) {
            setRouter(routerInstance);
          }
        } catch (error) {
          console.warn("Router not available:", error);
          // Router not available, continue without it
        }
      });
    }
  }, []);

  return router;
}

// Client-side wrapper to handle abort signals with server actions
async function clientActionWrapper(props: any) {
  const { signal, ...serverActionProps } = props;

  return new Promise((resolve, reject) => {
    // Check if already aborted
    if (signal?.aborted) {
      reject(new Error("Request was cancelled"));
      return;
    }

    // Create a promise that resolves with the server action
    const serverActionPromise = mutateAction(serverActionProps);

    // Create a promise that rejects when aborted
    const abortPromise = new Promise((_, abortReject) => {
      if (signal) {
        signal.addEventListener("abort", () => {
          abortReject(new Error("Request was cancelled"));
        });
      }
    });

    // Race between the server action and the abort signal
    Promise.race([serverActionPromise, abortPromise])
      .then(resolve)
      .catch(reject);
  });
}

interface FileInfo {
  s3ObjectKey: string;
  fileSize: number;
  status: "ACTIVE" | "IN_ACTIVE";
  originalFileName: string;
  objectKey?: string;
  uploadStatus: "pending" | "success" | "error";
  type?: string; // Optional file type
}

interface UseFileUploadProps {
  bucketType: string;
  maxSize?: number;
}

interface UseFileUploadReturn {
  uploadFiles: (
    files: FileList | null,
    key?: string
  ) => Promise<FileInfo[] | undefined>;
  isUploading: boolean;
  error: string | null;
  fileStatuses: Record<
    string,
    { status: "pending" | "success" | "error" | "aborted"; message?: string }
  >;
  abortFileUpload: (fileName: string) => void;
  abortAllUploads: () => void;
  setFileStatuses: any;
}

export const useFileUpload = ({
  bucketType,
  maxSize,
}: UseFileUploadProps): UseFileUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileStatuses, setFileStatuses] = useState<
    Record<
      string,
      { status: "pending" | "success" | "error" | "aborted"; message?: string }
    >
  >({});
  const { data: session, update } = useSession();
  const router = useSafeRouter(); // Use safe router hook

  // Helper function to extract file name without extension
  const getProcessedFileName = useCallback((file: File) => {
    const pattern = /[^a-zA-Z0-9 _.-]/g;
    const fileExtension = file.name.split(".").pop() || "";
    const fileNameWithoutExtension = file.name.substring(
      0,
      file.name.lastIndexOf(".")
    );
    const fileName = fileNameWithoutExtension;
    const fullName = `${fileName}.${fileExtension}`;
    return { fileName, fileExtension, fullName };
  }, []);

  // Keep track of ongoing uploads and abort controllers
  const fileAbortControllersRef = useRef<Record<string, AbortController>>({});
  const activeUploadsRef = useRef<Map<string, Promise<FileInfo>>>(new Map());
  const uploadQueueRef = useRef<Array<{ file: File; key?: string }>>([]);
  const isProcessingRef = useRef(false);

  // Add a ref to track files with terminal states (aborted/error/success)
  const terminalStatusFilesRef = useRef<Set<string>>(new Set());

  // Initialize terminal status from localStorage if available
  useEffect(() => {
    try {
      const storedTerminalFiles = localStorage.getItem(
        "terminal_file_statuses"
      );
      if (storedTerminalFiles) {
        const parsedFiles = JSON.parse(storedTerminalFiles);
        terminalStatusFilesRef.current = new Set(parsedFiles);

        // Update the file statuses state based on stored terminal states
        setFileStatuses((prev) => {
          const updatedStatuses = { ...prev };
          terminalStatusFilesRef.current.forEach((fileName) => {
            // Only update if it's not already set in the state
            if (!prev[fileName]) {
              // Default to aborted if we don't know the exact state
              updatedStatuses[fileName] = {
                status: "aborted",
                message: "Previous upload was cancelled",
              };
            }
          });
          return updatedStatuses;
        });
      }
    } catch (error) {
      console.error("Error loading terminal file statuses:", error);
    }
  }, []);

  // Helper function to mark a file as terminal and persist
  const markFileAsTerminal = useCallback(
    (
      fileName: string,
      status: "success" | "error" | "aborted",
      message?: string
    ) => {
      console.log(
        `Marking file as terminal: ${fileName} with status: ${status}`
      );

      // Update the state
      setFileStatuses((prev) => ({
        ...prev,
        [fileName]: { status, message },
      }));

      // Add to terminal status tracking
      terminalStatusFilesRef.current.add(fileName);

      // Persist to localStorage
      try {
        const terminalFilesArray = Array.from(terminalStatusFilesRef.current);
        localStorage.setItem(
          "terminal_file_statuses",
          JSON.stringify(terminalFilesArray)
        );
      } catch (error) {
        console.error("Error saving terminal file status:", error);
      }

      // Remove any pending controller
      if (fileAbortControllersRef.current[fileName]) {
        delete fileAbortControllersRef.current[fileName];
      }
    },
    []
  );

  // Function to check if file is in terminal state
  const isFileTerminal = useCallback((fileName: string) => {
    return terminalStatusFilesRef.current.has(fileName);
  }, []); // Upload single file function
  const uploadSingleFile = useCallback(
    async (file: File, key?: string): Promise<FileInfo> => {
      const { fileName, fileExtension, fullName } = getProcessedFileName(file);

      // Check if already in terminal state
      if (isFileTerminal(fullName)) {
        console.log(
          `File ${fullName} is already in terminal state, skipping upload`
        );
        throw new Error("File upload was previously completed or cancelled");
      }

      // Create individual AbortController for this file
      const controller = new AbortController();
      fileAbortControllersRef.current[fullName] = controller;
      const signal = controller.signal;

      try {
        // Check if already aborted before starting upload
        if (signal.aborted) {
          throw new Error("Upload was cancelled");
        }
        const newFile = new File([file], `${fileName}.${fileExtension}`, {
          type: file.type,
        });
        const formData = new FormData();
        formData.append("files", newFile);

        const randomText = randomStringGen("AlphaNumeric", 5);
        const fileRandomName = `${randomText}_${fileName}.${fileExtension}`;
        const fullPath = key ? `${key}/${fileRandomName}` : fileRandomName;

        // formData.append("keys", JSON.stringify([fullPath]));
        formData.append("bucket", bucketType);

        console.log(`Starting upload for: ${fileName}`);

        // Use clientActionWrapper to maintain abort functionality
        const response: any = await clientActionWrapper({
          data: formData,
          type: "post",
          endPoint: "/files/upload",
          session,
          headersReqWithSession: {
            "Content-Type": "multipart/form-data",
          },
          update,
          signal, // Pass the individual signal to the wrapper
        });

        if (response.error) {
          throw new Error(response.error.message || "Upload failed");
        }

        // Check if aborted after successful response
        if (signal.aborted) {
          throw new Error("Upload was cancelled");
        }

        console.log(`Upload completed for: ${fileName}`);

        // Mark as terminal with success status
        markFileAsTerminal(fullName, "success");

        return {
          s3ObjectKey: response.fileNames[0]?.objectKey || "",
          fileSize: file.size,
          status: "ACTIVE",
          originalFileName: fileName,
          objectKey: response.fileNames[0]?.objectKey || "",
          uploadStatus: "success",
          type: fileExtension,
        };
      } catch (err: any) {
        const isAborted =
          err.name === "AbortError" ||
          err.message?.includes("cancelled") ||
          signal.aborted;

        console.error(`Upload failed for ${fileName}:`, err);

        // Mark as terminal with appropriate status
        markFileAsTerminal(
          fullName,
          isAborted ? "aborted" : "error",
          isAborted ? "Upload cancelled" : err.message
        );

        throw err;
      }
    },
    [bucketType, session, update, markFileAsTerminal, isFileTerminal]
  );

  // Add a function to clear terminal file statuses from localStorage
  const clearTerminalFileStatuses = useCallback(() => {
    console.log("Clearing terminal file statuses from localStorage");
    terminalStatusFilesRef.current.clear();
    localStorage.removeItem("terminal_file_statuses");
  }, []);

  // Add function to clear all refs and statuses after successful uploads
  const clearAllRefsAfterSuccess = useCallback(() => {
    console.log("Clearing all refs after successful uploads");

    // Clear all refs except fileStatuses state
    fileAbortControllersRef.current = {};
    activeUploadsRef.current.clear();
    uploadQueueRef.current = [];
    isProcessingRef.current = false;
    terminalStatusFilesRef.current.clear();

    // Clear localStorage
    localStorage.removeItem("terminal_file_statuses");

    console.log("All refs cleared successfully");
  }, []);

  // Process upload queue with improved logging
  const processUploadQueue = useCallback(async () => {
    if (isProcessingRef.current || uploadQueueRef.current.length === 0) {
      return [];
    }

    console.log(
      "Processing upload queue with",
      uploadQueueRef.current.length,
      "files"
    );
    console.log("Terminal files:", Array.from(terminalStatusFilesRef.current));

    isProcessingRef.current = true;
    setIsUploading(true);

    const results: FileInfo[] = []; // Filter out files that are already in terminal states
    const initialQueueLength = uploadQueueRef.current.length;
    uploadQueueRef.current = uploadQueueRef.current.filter(({ file }) => {
      const { fileName, fileExtension } = getProcessedFileName(file);

      // Log with file extension info for debugging
      const shouldProcess = !isFileTerminal(fileName);
      if (!shouldProcess) {
        console.log(
          `Skipping ${fileName} (${fileExtension}) as it's in terminal state`
        );
      }
      return shouldProcess;
    });

    console.log(
      `Filtered queue from ${initialQueueLength} to ${uploadQueueRef.current.length} files`
    );

    while (uploadQueueRef.current.length > 0) {
      const { file, key } = uploadQueueRef.current.shift()!;

      try {
        const result = await uploadSingleFile(file, key);
        if (result) {
          results.push(result);
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        // Continue with next file even if one fails
      }
    }

    isProcessingRef.current = false;
    setIsUploading(false);

    // Check if all files have been processed successfully and clear all refs
    if (
      results.length > 0 &&
      results.every((file) => file.uploadStatus === "success")
    ) {
      clearAllRefsAfterSuccess();
    }

    return results;
  }, [uploadSingleFile, isFileTerminal, clearAllRefsAfterSuccess]);

  // Handle navigation - only if router is available
  useEffect(() => {
    if (!router?.events) {
      // Fallback to browser events if router not available
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (isProcessingRef.current) {
          event.preventDefault();
          event.returnValue =
            "Upload in progress. Are you sure you want to leave?";
          return event.returnValue;
        }
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          // Continue processing queue if needed
          if (uploadQueueRef.current.length > 0 && !isProcessingRef.current) {
            processUploadQueue();
          }
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }

    const handleRouteChangeStart = (url: string) => {
      console.log("Route changing to:", url);
      if (isProcessingRef.current) {
        console.log(
          "Upload in progress during navigation - continuing in background"
        );
      }
    };

    const handleRouteChangeComplete = (url: string) => {
      console.log("Route change completed:", url);
      if (uploadQueueRef.current.length > 0 && !isProcessingRef.current) {
        processUploadQueue();
      }
    };

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router, processUploadQueue]);

  // Improved upload files function
  const uploadFiles = useCallback(
    async (
      files: FileList | null,
      key?: string
    ): Promise<FileInfo[] | undefined> => {
      if (!files || files.length === 0) {
        setError("No files selected");
        return;
      }

      try {
        setError(null);
        console.log("Adding files to upload queue:", files.length);

        // Reset file statuses for new set of files
        setFileStatuses({});

        // Separate files into valid and invalid based on size
        const filesArray = Array.from(files);
        const validFiles: File[] = [];
        const oversizedFiles: File[] = [];

        if (maxSize) {
          filesArray.forEach((file) => {
            if (file.size > maxSize * 1024 * 1024) {
              oversizedFiles.push(file);
            } else {
              validFiles.push(file);
            }
          }); // Set error status for oversized files
          if (oversizedFiles.length > 0) {
            const oversizedStatuses = oversizedFiles.reduce(
              (acc, file) => {
                const { fileName, fullName } = getProcessedFileName(file);

                acc[fullName] = {
                  status: "error" as const,
                  message: `File size exceeds ${maxSize}MB limit`,
                };
                return acc;
              },
              {} as Record<string, { status: "error"; message: string }>
            );

            setFileStatuses((prev) => ({
              ...prev,
              ...oversizedStatuses,
            }));

            if (validFiles.length > 0) {
              setError(
                `${oversizedFiles.length} file(s) skipped due to size limit. Uploading ${validFiles.length} valid file(s).`
              );
            } else {
              setError(`All files exceed the ${maxSize}MB size limit`);
              return [];
            }
          }
        } else {
          validFiles.push(...filesArray);
        }

        if (validFiles.length === 0) {
          return [];
        } // Remove files from terminal status if they're being uploaded again
        validFiles.forEach((file) => {
          const { fileName, fileExtension, fullName } =
            getProcessedFileName(file);

          if (terminalStatusFilesRef.current.has(fullName)) {
            console.log(
              `Removing ${fileName}.${fileExtension} from terminal status as it's being uploaded again`
            );
            terminalStatusFilesRef.current.delete(fullName);

            // Update localStorage with the new terminal status list
            try {
              const terminalFilesArray = Array.from(
                terminalStatusFilesRef.current
              );
              localStorage.setItem(
                "terminal_file_statuses",
                JSON.stringify(terminalFilesArray)
              );
            } catch (error) {
              console.error("Error updating terminal file statuses:", error);
            }
          }
        }); // Set status to pending for all files being uploaded
        const pendingStatuses: Record<string, { status: "pending" }> = {};
        const filteredValidFiles: File[] = [];

        validFiles.forEach((file) => {
          const { fileName, fullName } = getProcessedFileName(file);
          // Don't check isFileTerminal here - we've already removed them from terminal status
          pendingStatuses[fullName] = { status: "pending" };
          filteredValidFiles.push(file);
        });
        setFileStatuses((prev) => {
          // Create a new state object without the files being uploaded again
          const updatedStatuses = { ...prev };
          filteredValidFiles.forEach((file) => {
            const { fileName, fullName } = getProcessedFileName(file);

            // Replace any existing status with pending
            updatedStatuses[fullName] = { status: "pending" };
          });
          return updatedStatuses;
        });

        // Add files to queue
        uploadQueueRef.current = [
          ...uploadQueueRef.current,
          ...filteredValidFiles.map((file) => ({ file, key })),
        ];

        // Start processing queue and return results
        const results = await processUploadQueue();

        // If all files were uploaded successfully, clear all refs
        if (
          results &&
          results.length === filteredValidFiles.length &&
          results.every((file) => file.uploadStatus === "success")
        ) {
          clearAllRefsAfterSuccess();
        }

        return results;
      } catch (err: any) {
        setError(err.message || "An error occurred during upload");
        console.error("Upload error:", err);
        return undefined;
      }
    },
    [processUploadQueue, maxSize, isFileTerminal, clearAllRefsAfterSuccess]
  );

  // Improved abort function
  const abortFileUpload = useCallback(
    (fileName: string) => {
      console.log(`Aborting upload for ${fileName}`);
      const controller = fileAbortControllersRef.current[fileName];
      if (controller) {
        controller.abort();
        markFileAsTerminal(fileName, "aborted", "Upload cancelled");
      } else {
        console.log(`No active controller found for ${fileName}`);
        // Still mark as terminal if there's no controller
        markFileAsTerminal(fileName, "aborted", "Upload cancelled");
      }
    },
    [markFileAsTerminal]
  );

  const abortAllUploads = useCallback(() => {
    Object.keys(fileAbortControllersRef.current).forEach((fileName) => {
      abortFileUpload(fileName);
    });

    // Clear the queue
    uploadQueueRef.current = [];
    setError("All uploads cancelled by user");
    setIsUploading(false);
    isProcessingRef.current = false;
  }, [abortFileUpload]);

  return {
    uploadFiles,
    isUploading,
    error,
    fileStatuses,
    abortFileUpload,
    abortAllUploads,
    setFileStatuses,
  };
};
